import React, { useCallback, useEffect, useState, useRef, lazy, memo, createElement, Component } from 'react';
import { CheckCircle2, XCircle, Clock, Star, FileText, Zap, ShieldCheck, Users, ChevronDown, ArrowRight, MessageSquare, Check, Beef, Calculator, TrendingUp, Percent, Utensils, Smartphone } from 'lucide-react';
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// --- CSS Animations (injected once) ---
const injectStyles = () => {
  if (document.getElementById('lp-perf-styles')) return;
  const style = document.createElement('style');
  style.id = 'lp-perf-styles';
  style.textContent = `
    @keyframes cta-pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.02); }
    }
    .cta-pulse { animation: cta-pulse 2s ease-in-out infinite; }
    @keyframes fade-in-up {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .fade-in-up { animation: fade-in-up 0.4s ease-out forwards; }
    @keyframes fade-out-down {
      from { opacity: 1; transform: translateY(0); }
      to { opacity: 0; transform: translateY(20px); }
    }
    .fade-out-down { animation: fade-out-down 0.3s ease-in forwards; }
    .notif-enter { animation: fade-in-up 0.4s ease-out forwards; }
    .notif-exit { animation: fade-out-down 0.3s ease-in forwards; }
  `;
  document.head.appendChild(style);
};
// --- Sub-components ---
const CountdownTimer = memo(() => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 27,
    seconds: 35
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          ...prev,
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex gap-2 font-mono text-xl md:text-2xl font-bold text-white" data-id="element-1">
      <div className="bg-[#800000] px-2 py-1 rounded border border-white/20" data-id="element-2">
        {String(timeLeft.hours).padStart(2, '0')}h
      </div>
      <span className="text-white" data-id="element-3">:</span>
      <div className="bg-[#800000] px-2 py-1 rounded border border-white/20" data-id="element-4">
        {String(timeLeft.minutes).padStart(2, '0')}m
      </div>
      <span className="text-white" data-id="element-5">:</span>
      <div className="bg-[#800000] px-2 py-1 rounded border border-white/20" data-id="element-6">
        {String(timeLeft.seconds).padStart(2, '0')}s
      </div>
    </div>;
});
const FloatingNotification = memo(() => {
  const [visible, setVisible] = useState(false);
  const [animClass, setAnimClass] = useState('notif-enter');
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasScrolledRef = useRef(false);
  const notifications = [{
    n: 'Jorge L.',
    l: 'Buenos Aires',
    t: 'hace 2 min'
  }, {
    n: 'Ricardo M.',
    l: 'Córdoba',
    t: 'hace 5 min'
  }, {
    n: 'Carlos R.',
    l: 'Rosario',
    t: 'hace 8 min'
  }, {
    n: 'Daniel F.',
    l: 'Mendoza',
    t: 'hace 12 min'
  }, {
    n: 'Miguel A.',
    l: 'Salta',
    t: 'hace 15 min'
  }, {
    n: 'Sebastian G.',
    l: 'Tucumán',
    t: 'hace 20 min'
  }, {
    n: 'Facundo S.',
    l: 'Santa Fe',
    t: 'hace 25 min'
  }, {
    n: 'Matias P.',
    l: 'Mar del Plata',
    t: 'hace 30 min'
  }, {
    n: 'Lucas B.',
    l: 'La Plata',
    t: 'hace 45 min'
  }, {
    n: 'Cristian V.',
    l: 'Neuquén',
    t: 'hace 1 hora'
  }, {
    n: 'Alejandro M.',
    l: 'Posadas',
    t: 'hace 2 horas'
  }, {
    n: 'Roberto T.',
    l: 'Resistencia',
    t: 'hace 3 horas'
  }, {
    n: 'Sergio N.',
    l: 'Corrientes',
    t: 'hace 4 horas'
  }, {
    n: 'Gustavo D.',
    l: 'San Juan',
    t: 'hace 5 horas'
  }, {
    n: 'Marcelo C.',
    l: 'San Luis',
    t: 'hace 6 horas'
  }, {
    n: 'Eduardo F.',
    l: 'Catamarca',
    t: 'hace 7 horas'
  }, {
    n: 'Hugo R.',
    l: 'La Rioja',
    t: 'hace 8 horas'
  }];
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) hasScrolledRef.current = true;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const showNext = () => {
      if (!hasScrolledRef.current) return;
      setAnimClass('notif-enter');
      setVisible(true);
      setTimeout(() => {
        setAnimClass('notif-exit');
        setTimeout(() => setVisible(false), 300);
      }, 4000);
    };
    const initialDelay = setTimeout(() => {
      if (hasScrolledRef.current) showNext();
    }, 3000);
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % notifications.length);
      showNext();
    }, 12000);
    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, []);
  const current = notifications[currentIndex];
  if (!visible) return null;
  return <div className={`fixed bottom-6 left-6 z-50 bg-white shadow-2xl rounded-lg p-4 border border-slate-100 flex items-center gap-4 max-w-sm ${animClass}`} data-id="element-7">
      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center text-[#800000]" data-id="element-8">
        <Check size={20} data-id="element-9" />
      </div>
      <div data-id="element-10">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-11">
          Compra verificada
        </p>
        <p className="text-sm text-slate-800" data-id="element-12">
          <span className="font-bold" data-id="element-13">{current.n}</span> de {current.l}
          <br data-id="element-14" />
          compró <span className="font-bold" data-id="element-15">sistema completo</span> 🥩
        </p>
        <p className="text-xs text-slate-400 mt-1" data-id="element-16">{current.t}</p>
      </div>
      <button onClick={() => setVisible(false)} className="text-slate-300 hover:text-slate-500" aria-label="Cerrar notificación" data-id="element-17">
        <XCircle size={16} data-id="element-18" />
      </button>
    </div>;
});
const SectionTitle = memo(({
  children,
  subtitle,
  light = false
}: {
  children: React.ReactNode;
  subtitle?: string;
  light?: boolean;
}) => <div className="text-center mb-6 md:mb-10" data-id="element-19">
      <h2 className={`text-2xl md:text-5xl font-bold mb-2 md:mb-4 ${light ? 'text-white' : 'text-[#121212]'}`} data-id="element-20">
        {children}
      </h2>
      {subtitle && <p className={`text-sm md:text-lg ${light ? 'text-slate-200' : 'text-slate-600'} max-w-2xl mx-auto font-sans px-4`} data-id="element-21">
          {subtitle}
        </p>}
    </div>);
const CTAButton = memo(({
  children,
  secondary = false,
  className = '',
  href = '',
  onClick
}: {
  children: React.ReactNode;
  secondary?: boolean;
  className?: string;
  href?: string;
  onClick?: () => void;
}) => {
  const buttonEl = <button className={`
        cta-pulse w-full md:w-auto px-10 py-5 rounded-full font-bold text-lg transition-all transform hover:scale-105 active:scale-95 shadow-xl
        ${secondary ? 'bg-white text-[#800000] border-2 border-slate-200 hover:bg-slate-50' : 'bg-[#800000] text-white hover:bg-[#600000]'}
        ${className}
      `} onClick={onClick} data-id="element-22">
        {children}
      </button>;
  if (href) {
    const isInternal = href.startsWith('#');
    return <a href={href} target={isInternal ? undefined : '_blank'} rel={isInternal ? undefined : 'noopener noreferrer'} className="w-full md:w-auto block" onClick={e => {
      if (isInternal) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }} data-id="element-23">
          {buttonEl}
        </a>;
  }
  return buttonEl;
});
// Static data arrays (defined outside component to avoid re-creation)
const statsData = [{
  label: 'Carniceros',
  value: '+1.200'
}, {
  label: 'Módulos',
  value: '6'
}, {
  label: 'Páginas',
  value: '350+'
}, {
  label: 'Calificación',
  value: '4.9/5',
  stars: true
}];
const testimonialsData = [{
  n: 'Jorge L.',
  l: 'Buenos Aires',
  q: 'Con la calculadora del Bono 1 supe exactamente cómo acomodar los precios para no perder con la suba del lunes.',
  i: 'JL'
}, {
  n: 'Ricardo M.',
  l: 'Maestro Carnicero',
  q: 'Empecé a hacer mis propios chorizos premium y hoy son mi producto más vendido.',
  i: 'RM'
}, {
  n: 'Carlos R.',
  l: 'Rosario',
  q: 'Por fin algo que habla en argentino y entiende nuestra media res.',
  i: 'CR'
}];
const problemItems = ['Sin saber calcular el Costo de Reposición real.', 'Con mermas invisibles que se comen tu ganancia.', 'Perdiendo clientes por no ofrecer cortes de autor.', 'Con miedo a clausuras de Bromatología.'];
const modulesData = [{
  t: 'MÓDULO 1: LA BIBLIA DEL DESPOSTE',
  d: 'Aprovechamiento total de la res. Técnicas criollas para no desperdiciar ni un gramo.',
  iconName: 'Beef'
}, {
  t: 'MÓDULO 2: INGENIERÍA DE LA MERMA',
  d: 'Recuperación de grasas y huesos. Cómo convertir el desperdicio en dinero contante y sonante.',
  iconName: 'TrendingUp'
}, {
  t: 'MÓDULO 3: PSICOLOGÍA DEL MOSTRADOR',
  d: 'Cómo subir el ticket promedio. El arte de vender lo que a vos te conviene, no solo lo que el cliente pide.',
  iconName: 'Users'
}, {
  t: 'MÓDULO 4: LA VACA ENTERA',
  d: 'Rentabilidad en cortes de segunda. Secretos para que el osobuco y la falda rindan como lomo.',
  iconName: 'Utensils'
}];
const bonusesData = [{
  t: 'BONO 1: CALCULADORA DE COSTOS PRO',
  d: 'Planilla Anti-Inflación. Ponés el precio del kilo vivo y te da el costo de cada corte.',
  iconName: 'Calculator'
}, {
  t: 'BONO 2: MANUAL CARNICERÍA BOUTIQUE',
  d: 'Tomahawk y Cortes de Selección. Cómo cobrar el doble por lo mismo.',
  iconName: 'Star'
}, {
  t: 'BONO 3: FACTURA Y ELABORADOS',
  d: 'Chorizos y Milanesas de Autor. El margen real está en lo que vos fabricás.',
  iconName: 'Utensils'
}, {
  t: 'BONO 4: PROTOCOLOS ANTI-CLAUSURA',
  d: 'Checklist de Inocuidad. Que Bromatología entre y salga sin anotar nada.',
  iconName: 'ShieldCheck'
}, {
  t: 'BONO 5: WHATSAPP CÁRNICO',
  d: 'Venta digital por estados y grupos. Tu carnicería abierta las 24 horas.',
  iconName: 'Smartphone'
}];
const pricingFeatures = ['Módulos de Desposte y Merma', 'Calculadora de Costos Anti-Inflación', 'Manual de Cortes Boutique', 'Protocolos de Inocuidad', 'Estrategia de Venta por WhatsApp', 'Acceso inmediato para siempre'];
const comparisonData = [{
  s: 'Sube el frigorífico',
  no: 'Perdés margen o clientes',
  yes: 'Blindás tu ganancia con la calculadora.'
}, {
  s: 'Bromatología visita',
  no: 'Miedo y riesgo de multas',
  yes: 'Protocolo Bono 4: Todo en regla.'
}, {
  s: 'Sobran recortes',
  no: 'Se tiran o regalan',
  yes: 'Bono 3: Se hacen hamburguesas gourmet.'
}];
const iconMap: Record<string, ComponentType<any>> = {
  Beef,
  TrendingUp,
  Users,
  Utensils,
  Calculator,
  Star,
  ShieldCheck,
  Smartphone
};
// --- Main Page ---
export function LandingPage() {
  useEffect(() => {
    injectStyles();
  }, []);
  useEffect(() => {
    // Meta Pixel Code
    if (!window.fbq) {
      const n: any = window.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!window._fbq) window._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      const t = document.createElement('script');
      t.async = !0;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(t);
    }
    window.fbq('init', '929826529816985');
    window.fbq('track', 'PageView');
  }, []);
  return <div className="min-h-screen font-sans bg-[#F9F6F2]" data-id="element-24">
      <FloatingNotification data-id="element-25" />

      {/* Top Banner */}
      <div className="bg-[#800000] text-white text-center py-1.5 px-4 text-[10px] md:text-sm font-bold uppercase tracking-widest relative z-20" data-id="element-26">
        🚨 ATENCIÓN MAESTROS CARNICEROS: El precio promocional por lanzamiento
        de la Edición 2026 finaliza hoy.
      </div>

      {/* Hero Section */}
      <header className="relative bg-[#121212] md:min-h-[90svh] md:h-auto flex flex-col text-white overflow-hidden" data-id="element-27">
        <div className="absolute inset-0 opacity-20" data-id="element-28">
          <div className="absolute inset-0 bg-gradient-to-b from-[#800000]/20 to-transparent" data-id="element-29"></div>
        </div>

        <div className="container mx-auto px-4 flex-grow flex flex-col justify-center py-8 md:py-16 relative z-10" data-id="element-30">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center h-full justify-center" data-id="element-31">
            <div className="flex flex-col items-center" data-id="element-32">
              <div className="fade-in-up inline-flex items-center gap-2 bg-[#800000]/20 border border-[#800000]/40 px-3 py-0.5 rounded-full text-[#F9F6F2] text-[9px] md:text-sm font-bold mb-2 md:mb-8 backdrop-blur-sm" data-id="element-33">
                🥩 SISTEMA CARNICERÍAS RENTABLES 2026™
              </div>

              <h1 className="text-[24px] md:text-7xl font-bold mb-2 md:mb-8 text-white leading-tight" data-id="element-34">
                La Biblia del Maestro Carnicero.
              </h1>

              <p className="text-sm md:text-2xl text-white/80 mb-4 md:mb-8 max-w-3xl mx-auto leading-relaxed px-4 italic" data-id="element-35">
                El método probado para defender tu margen, optimizar el desposte
                y blindar tu negocio contra la inflación.
              </p>
            </div>

            <div className="relative max-w-[240px] md:max-w-3xl mx-auto mb-4 md:mb-12" data-id="element-36">
              <div className="relative z-10 bg-gradient-to-br from-zinc-800 to-zinc-900 p-2 md:p-4 rounded-xl md:rounded-2xl shadow-2xl border border-zinc-700" data-id="element-37">
                <img src="https://cdn.magicpatterns.com/uploads/gukH4ts1JW6siAhQCCrm1g/a_well_lit_indoor_butcher_shop_scene_overall_view_1_web.webp" alt="Sistema Carnicerías Rentables Mockup" className="w-full h-auto rounded-lg md:rounded-xl shadow-lg" width={1200} height={800} fetchPriority="high" decoding="async" data-id="element-38" />
                <div className="absolute -bottom-2 -right-2 bg-[#800000] text-white px-2 py-1 md:p-4 rounded-lg shadow-xl font-bold text-[10px] md:text-base" data-id="element-39">
                  EDICIÓN 2026
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center w-full" data-id="element-40">
              <CTAButton href="#pricing" className="uppercase tracking-wide px-6 py-3 md:px-12 md:py-6 text-sm md:text-xl mb-3 md:mb-6" data-id="element-41">
                → QUIERO PROFESIONALIZAR MI CARNICERÍA HOY
              </CTAButton>

              <div className="flex justify-center gap-3 md:gap-6 text-white/60 font-medium text-[8px] md:text-sm mb-4 md:mb-0" data-id="element-42">
                <div className="flex items-center gap-1 bg-white/5 px-2 py-0.5 rounded-full border border-white/10" data-id="element-43">
                  +350 Páginas prácticas
                </div>
                <div className="flex items-center gap-1 bg-white/5 px-2 py-0.5 rounded-full border border-white/10" data-id="element-44">
                  <Zap size={10} className="md:w-3.5 md:h-3.5" data-id="element-45" /> Acceso
                  Instantáneo
                </div>
                <div className="flex items-center gap-1 bg-white/5 px-2 py-0.5 rounded-full border border-white/10" data-id="element-46">
                  <ShieldCheck size={10} className="md:w-3.5 md:h-3.5" data-id="element-47" />{' '}
                  Garantía 7 días
                </div>
              </div>
            </div>

            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-2 md:p-6 shadow-2xl inline-block self-center hidden md:block" data-id="element-48">
              <p className="text-white/60 font-bold uppercase tracking-widest text-[10px] md:text-xs mb-2 md:mb-3" data-id="element-49">
                ⏰ EL PRECIO PROMOCIONAL CIERRA EN:
              </p>
              <CountdownTimer data-id="element-50" />
            </div>
          </div>
        </div>
      </header>

      {/* Stats Section */}
      <section className="py-6 md:py-12 bg-[#800000] text-white" data-id="element-51">
        <div className="container mx-auto px-4" data-id="element-52">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-4xl mx-auto text-center" data-id="element-53">
            {statsData.map((stat, i) => <div key={i} data-id="element-54">
                <p className="text-3xl md:text-4xl font-bold mb-1" data-id="element-55">
                  {stat.value}
                </p>
                {stat.stars && <div className="flex justify-center text-yellow-500 mb-1" data-id="element-56">
                    {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" data-id="element-57" />)}
                  </div>}
                <p className="text-[10px] md:text-xs uppercase tracking-widest font-bold opacity-80" data-id="element-58">
                  {stat.label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10 md:py-24 bg-white" data-id="element-59">
        <div className="container mx-auto px-4" data-id="element-60">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto" data-id="element-61">
            {testimonialsData.map((test, i) => <div key={i} className="bg-[#F9F6F2] p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-62">
                <div className="flex gap-1 mb-4 text-[#800000]" data-id="element-63">
                  {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-64" />)}
                </div>
                <p className="text-[#121212] italic mb-6 leading-relaxed" data-id="element-65">
                  "{test.q}"
                </p>
                <div className="flex items-center gap-4" data-id="element-66">
                  <div className="w-10 h-10 bg-[#800000] rounded-full flex items-center justify-center font-bold text-white" data-id="element-67">
                    {test.i}
                  </div>
                  <div data-id="element-68">
                    <p className="font-bold text-[#121212]" data-id="element-69">{test.n}</p>
                    <p className="text-xs text-slate-500" data-id="element-70">{test.l}</p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-10 md:py-24 bg-[#121212] text-white" data-id="element-71">
        <div className="container mx-auto px-4" data-id="element-72">
          <SectionTitle light subtitle="Baja el camión. Los precios subieron. Los clientes se quejan. No es falta de oficio, es falta de un sistema de gestión real." data-id="element-73">
            ¿Cuántas medias reses vas a despostar sintiendo que el dinero se te
            escapa de las manos?
          </SectionTitle>

          <div className="grid md:grid-cols-2 gap-3 md:gap-4 max-w-4xl mx-auto mt-8 md:mt-12" data-id="element-74">
            {problemItems.map((item, i) => <div key={i} className="bg-white/5 border border-white/10 p-4 md:p-6 rounded-xl flex gap-3 md:gap-4 items-start" data-id="element-75">
                <XCircle className="text-[#800000] shrink-0 mt-1" size={20} data-id="element-76" />
                <p className="text-white/90 font-medium text-base md:text-lg" data-id="element-77">
                  {item}
                </p>
              </div>)}
          </div>

          <div className="mt-10 md:mt-16 max-w-3xl mx-auto bg-[#800000]/10 border-l-8 border-[#800000] p-6 md:p-8 rounded-r-2xl shadow-sm text-center" data-id="element-78">
            <p className="text-2xl md:text-3xl font-bold text-white italic" data-id="element-79">
              ¿Y si esta fuera la última vez que enfrentás una suba de precios
              sin saber qué hacer?
            </p>
          </div>
        </div>
      </section>

      {/* Product Hero */}
      <section className="py-10 md:py-24 bg-white" data-id="element-80">
        <div className="container mx-auto px-4 text-center" data-id="element-81">
          <div className="inline-block bg-[#800000] text-white px-5 py-1.5 rounded-full font-bold text-xs uppercase tracking-widest mb-6 md:mb-8" data-id="element-82">
            SOLUCIÓN INTEGRAL
          </div>
          <h2 className="text-3xl md:text-6xl font-bold mb-4 md:mb-6 text-[#121212]" data-id="element-83">
            🥩 SISTEMA CARNICERÍAS RENTABLES 2026™
          </h2>
          <p className="text-base md:text-2xl text-slate-600 mb-8 md:mb-12 max-w-3xl mx-auto" data-id="element-84">
            Ebook Central + 5 Bonos Estratégicos. 100% adaptado a la realidad de
            Argentina y LATAM. No más improvisación.
          </p>

          <div className="max-w-4xl mx-auto mb-8 md:mb-16 relative" data-id="element-85">
            <img src="https://images.unsplash.com/photo-1551028150-64b9f398f678?auto=format&fit=crop&q=80&w=800" alt="Pack Completo" className="w-full h-auto rounded-2xl md:rounded-3xl shadow-2xl border border-slate-200" loading="lazy" decoding="async" width={800} height={533} data-id="element-86" />
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-10 md:py-24 bg-[#F9F6F2]" data-id="element-87">
        <div className="container mx-auto px-4" data-id="element-88">
          <SectionTitle subtitle="La estructura maestra para transformar tu negocio." data-id="element-89">
            📚 MÓDULOS DEL EBOOK CENTRAL
          </SectionTitle>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto" data-id="element-90">
            {modulesData.map((m, i) => {
            const IconComp = iconMap[m.iconName];
            return <div key={i} className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-[#800000]" data-id="element-91">
                  <div className="text-[#800000] mb-4" data-id="element-92">
                    {IconComp && <IconComp size={24} data-id="element-93" />}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-[#121212]" data-id="element-94">
                    {m.t}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-95">{m.d}</p>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-10 md:py-24 bg-white" data-id="element-96">
        <div className="container mx-auto px-4" data-id="element-97">
          <SectionTitle subtitle="Valor total bonos: $55.000 → GRATIS con tu pack" data-id="element-98">
            🎁 5 BONOS ESTRATÉGICOS
          </SectionTitle>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto" data-id="element-99">
            {bonusesData.map((b, i) => {
            const IconComp = iconMap[b.iconName];
            return <div key={i} className="bg-[#F9F6F2] p-6 rounded-2xl border border-slate-100 relative overflow-hidden" data-id="element-100">
                  <div className="absolute top-0 right-0 bg-[#800000] text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase" data-id="element-101">
                    Gratis
                  </div>
                  <div className="text-[#800000] mb-4" data-id="element-102">
                    {IconComp && <IconComp size={24} data-id="element-103" />}
                  </div>
                  <h4 className="text-lg font-bold mb-2 text-[#121212]" data-id="element-104">
                    {b.t}
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-105">
                    {b.d}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-6 md:py-16 bg-[#121212] text-white" id="pricing" data-id="element-106">
        <div className="container mx-auto px-4" data-id="element-107">
          <div className="text-center mb-4 md:mb-8" data-id="element-108">
            <h2 className="text-2xl md:text-5xl font-bold mb-2 text-white" data-id="element-109">
              LLEVATE TU PACK COMPLETO ⭐
            </h2>
            <p className="text-sm md:text-lg text-slate-200 max-w-2xl mx-auto font-sans px-4" data-id="element-110">
              Acceso inmediato a todo el material en formato PDF.
            </p>
          </div>

          <div className="max-w-2xl mx-auto" data-id="element-111">
            <div className="bg-[#1a1a1a] rounded-2xl md:rounded-3xl p-5 md:p-10 text-white shadow-2xl flex flex-col border-4 border-[#800000] relative" data-id="element-112">
              <div className="text-center mb-3 md:mb-6" data-id="element-113">
                <h3 className="text-lg md:text-2xl font-bold mb-1" data-id="element-114">
                  SISTEMA CARNICERÍAS RENTABLES
                </h3>
                <p className="text-white/60 text-[10px] md:text-xs" data-id="element-115">
                  Ebook Central + 5 Bonos
                </p>
              </div>

              <div className="text-center mb-3 md:mb-6" data-id="element-116">
                <p className="text-zinc-500 line-through text-xs md:text-lg mb-0.5" data-id="element-117">
                  $65.000 ARS
                </p>
                <p className="text-3xl md:text-6xl font-bold text-[#800000]" data-id="element-118">
                  $19.990
                </p>
                <p className="text-xs md:text-lg font-medium" data-id="element-119">ARS</p>
              </div>

              <div className="flex flex-col items-center mb-4 md:mb-8" data-id="element-120">
                <p className="text-white/60 font-bold uppercase tracking-widest text-[9px] md:text-[10px] mb-2" data-id="element-121">
                  ⏰ LA OFERTA TERMINA EN:
                </p>
                <CountdownTimer data-id="element-122" />
              </div>

              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 md:gap-y-3 mb-5 md:mb-8" data-id="element-123">
                {pricingFeatures.map((item, i) => <li key={i} className="flex items-center gap-2 text-[11px] md:text-sm font-medium" data-id="element-124">
                    <CheckCircle2 size={14} className="text-[#800000] shrink-0 md:w-4 md:h-4" data-id="element-125" />
                    <span data-id="element-126">{item}</span>
                  </li>)}
              </ul>

              <CTAButton href="https://checkout.guiadigital360.online/cart/44952300519493:1" className="w-full py-3.5 md:py-5 text-base md:text-xl" data-id="element-127">
                → QUIERO EL SISTEMA COMPLETO AHORA
              </CTAButton>

              <p className="mt-3 md:mt-5 text-center text-[#800000] font-bold text-[9px] md:text-sm" data-id="element-128">
                ⚠️ Después del contador, el precio sube $10.000.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-10 md:py-24 bg-white" data-id="element-129">
        <div className="container mx-auto px-4" data-id="element-130">
          <SectionTitle subtitle="La diferencia entre un negocio que sobrevive y uno que prospera." data-id="element-131">
            ⚖️ ¿CUÁNTO CUESTA NO TENERLO?
          </SectionTitle>

          {/* Mobile View */}
          <div className="block md:hidden space-y-4 max-w-sm mx-auto" data-id="element-132">
            {comparisonData.map((row, i) => <div key={i} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-133">
                <div className="bg-[#121212] px-4 py-2 text-xs font-bold text-white uppercase tracking-wider" data-id="element-134">
                  {row.s}
                </div>
                <div className="grid grid-cols-2 text-[11px] leading-tight" data-id="element-135">
                  <div className="bg-red-50 p-4 text-red-700 italic border-r border-white" data-id="element-136">
                    <span className="block font-bold text-[9px] uppercase mb-1 opacity-60" data-id="element-137">
                      Sin el Sistema
                    </span>
                    {row.no}
                  </div>
                  <div className="bg-green-50 p-4 text-green-800 font-bold" data-id="element-138">
                    <span className="block font-bold text-[9px] uppercase mb-1 opacity-60" data-id="element-139">
                      Con el Sistema
                    </span>
                    {row.yes}
                  </div>
                </div>
              </div>)}
          </div>

          {/* Desktop View */}
          <div className="hidden md:block max-w-4xl mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xl" data-id="element-140">
            <table className="w-full text-left border-collapse" data-id="element-141">
              <thead data-id="element-142">
                <tr className="bg-[#121212] text-white" data-id="element-143">
                  <th className="p-6 font-bold" data-id="element-144">SITUACIÓN</th>
                  <th className="p-6 font-bold" data-id="element-145">❌ SIN EL SISTEMA</th>
                  <th className="p-6 font-bold" data-id="element-146">✅ CON EL SISTEMA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-147">
                {comparisonData.map((row, i) => <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-148">
                    <td className="p-6 text-sm font-bold text-[#121212]" data-id="element-149">
                      {row.s}
                    </td>
                    <td className="p-6 text-sm text-red-700 italic" data-id="element-150">
                      {row.no}
                    </td>
                    <td className="p-6 text-sm font-bold text-green-700" data-id="element-151">
                      {row.yes}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-10 md:py-24 bg-[#800000]/5" data-id="element-152">
        <div className="container mx-auto px-4 text-center" data-id="element-153">
          <div className="max-w-3xl mx-auto bg-white p-8 md:p-16 rounded-2xl md:rounded-3xl border-2 border-[#800000] shadow-xl" data-id="element-154">
            <ShieldCheck size={60} className="text-[#800000] mx-auto mb-6 md:mb-8" data-id="element-155" />
            <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6 text-[#121212]" data-id="element-156">
              GARANTÍA DE SATISFACCIÓN DE 7 DÍAS
            </h2>
            <p className="text-lg md:text-xl text-slate-600 mb-6 md:mb-8 leading-relaxed" data-id="element-157">
              Si el material no mejora tu rentabilidad, te devolvemos el 100%.{' '}
              <br data-id="element-158" />
              <span className="text-xl md:text-2xl font-bold text-[#800000] block mt-2 md:mt-4 uppercase" data-id="element-159">
                Sin preguntas.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 md:py-16 bg-[#121212] text-white/40 border-t border-white/5" data-id="element-160">
        <div className="container mx-auto px-4 text-center" data-id="element-161">
          <p className="font-bold text-white text-2xl mb-4" data-id="element-162">
            🥩 SISTEMA CARNICERÍAS RENTABLES™
          </p>
          <p className="text-sm mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-163">
            El método definitivo para el carnicero moderno que busca
            profesionalizar su oficio y proteger su capital.
          </p>
          <p className="text-xs" data-id="element-164">© 2026 — Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>;
}